import {
  useInfiniteQueryUserMemberAccessLog,
  useMutationKisiResetAccess,
} from "@gymflow/api";
import { UserMemberAccessLogItem } from "@gymflow/types";
import { useQuery } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import { usePageSize } from "apps/portal/src/hooks";
import { ModalContext, useClubSettings } from "apps/portal/src/providers";
import { ToastContext } from "apps/portal/src/providers/ToastProvider/context";
import useGymflowModels from "apps/portal/src/store";
import { apiV2 } from "libs/api/src/lib/apiV2";
import { useCallback, useContext, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "../../atoms";
import { PaginatedTable } from "../../organisms";
import { SettingsContainer } from "../../Settings/SettingsContainer";
import { ConfirmModal } from "../../templates";
import { formatDate } from "./utils/formatDate";

type UserMemberVisitsProps = {
  memberId: string;
};

export const UserMemberVisits = ({ memberId }: UserMemberVisitsProps) => {
  const { api } = useGymflowModels();
  const { data: member } = useQuery(
    apiV2.staff.member.queries.idDeprecated(memberId),
  );
  const [currentPage, setCurrentPage] = useState(0);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();
  const { toast } = useContext(ToastContext);
  const { setModal, hide } = useContext(ModalContext);
  const { club } = useClubSettings();
  const columnHelper = createColumnHelper<UserMemberAccessLogItem>();
  const pageSize = usePageSize({
    tableContainerRef,
    rowHeight: 56,
  });

  const {
    data: accessLog,
    isFetching,
    fetchNextPage,
  } = useInfiniteQueryUserMemberAccessLog({
    api,
    params: { userMemberId: memberId, limit: pageSize },
  });

  const resetAccessMutation = useMutationKisiResetAccess({ api });

  const columns = [
    columnHelper.accessor("checkInDate", {
      id: "checkInDate",
      header: () =>
        t("page.userMember.tab.userMemberVisits.table.header.checkInDate"),
      enableSorting: false,
      cell: (cell) => (
        <div className="text-sm font-medium text-gray-950">
          {formatDate(cell.getValue(), club.timezone)}
        </div>
      ),
    }),
    columnHelper.accessor("checkOutDate", {
      id: "checkOutDate",
      header: () =>
        t("page.userMember.tab.userMemberVisits.table.header.checkOutDate"),
      enableSorting: false,
      cell: (cell) => (
        <div className="text-sm font-medium text-gray-950">
          {formatDate(cell.getValue(), club.timezone)}
        </div>
      ),
    }),
  ];

  const resetAccess = useCallback(
    async (isReset: boolean) => {
      await resetAccessMutation.mutateAsync({
        memberId,
      });
      toast({
        message: isReset
          ? t(
              "page.userMember.tab.userMemberVisits.modal.resetAccess.toast.success",
            )
          : t(
              "page.userMember.tab.userMemberVisits.modal.setupAccess.toast.success",
            ),
      });
      hide();
    },
    [hide, resetAccessMutation, t, toast, memberId],
  );

  const showResetAccessModal = useCallback(
    (isReset: boolean) =>
      setModal(
        <ConfirmModal
          onHide={() => hide()}
          onCancel={() => hide()}
          onConfirm={async () => await resetAccess(isReset)}
          confirmText={t(
            "page.userMember.tab.userMemberVisits.modal.confirmText",
          )}
          title={
            isReset
              ? t(
                  "page.userMember.tab.userMemberVisits.modal.resetAccess.title",
                )
              : t(
                  "page.userMember.tab.userMemberVisits.modal.setupAccess.title",
                )
          }
        >
          {isReset
            ? t(
                "page.userMember.tab.userMemberVisits.modal.resetAccess.description",
              )
            : t(
                "page.userMember.tab.userMemberVisits.modal.setupAccess.description",
              )}
        </ConfirmModal>,
      ),
    [hide, resetAccess, setModal, t],
  );

  const currentPageData = accessLog?.pages[currentPage]?.content ?? [];

  return (
    <SettingsContainer
      title={t("page.userMember.tab.userMemberVisits.title")}
      actions={
        club?.kisiStatus === "ENABLED" && (
          <Button
            intent="error-outline"
            onClick={() => showResetAccessModal(!!member?.kisiUserId)}
          >
            {member?.kisiUserId
              ? t("page.userMember.tab.userMemberVisits.button.resetAccess")
              : t("page.userMember.tab.userMemberVisits.button.setupAccess")}
          </Button>
        )
      }
    >
      <PaginatedTable
        tableProps={{
          data: currentPageData,
          columns,
          isFetching,
          pageSize,
          tableContainerRef,
        }}
        hasPreviousPage={!!accessLog && currentPage > 0}
        hasNextPage={
          !!accessLog && !!accessLog.pages[currentPage]?.nextPageToken
        }
        goToNextPage={() => {
          setCurrentPage((e) => e + 1);
          if (!accessLog?.pages[currentPage + 1]) {
            fetchNextPage();
          }
        }}
        goToPreviousPage={() => {
          setCurrentPage((e) => e - 1);
        }}
      />
    </SettingsContainer>
  );
};
